import { CopyIcon, PencilIcon, PlusIcon, StarIcon, UsersIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardPreset } from '@/entities/dashboards';
import { getPresetAccess, useDashboardPresets } from '@/entities/settings';
import { SharePresetDialog } from '@/entities/shared-presets';
import { cn } from '@/shared/lib';
import { ConfirmationButton, IconButton } from '@/shared/ui';

type Props = {
  /**
   * Применённый дашборд сменили (сохранили копию) или сняли (удалили): страница показывает
   * новый либо возвращается к стандартному, а если она была открыта разделом прежнего
   * дашборда — уходит и с его адреса.
   */
  onActivePresetChange: (presetId: string | null) => void;
  /** Завести новый дашборд: страница включает режим редактирования на пустой сетке */
  onCreatePreset: () => void;
  /** Править состав применённого дашборда: страница включает режим редактирования на его сетке */
  onEditPreset: (preset: DashboardPreset) => void;
};

/**
 * Действия над применённым дашбордом: завести новый, редактировать, сделать системным, поделиться,
 * закрепить в меню, удалить. Без применённого дашборда остаётся только заведение нового — править и
 * закреплять нечего.
 *
 * Действие, которого пользователь над этим дашбордом выполнить не вправе, не показано. Получатель
 * совместного дашборда и не автор системного (даже с разрешением на системные пресеты) его не правят:
 * вместо правки они сохраняют личную копию. Получатель закрепляет совместный у себя и убирает его только
 * из своего списка; системный закрепляется и удаляется у всех, поэтому у не автора этих действий нет.
 * Системным дашбордом не делятся — он и так виден всем.
 */
export const DashboardPresetActions: FC<Props> = ({ onActivePresetChange, onCreatePreset, onEditPreset }) => {
  const { t } = useTranslation('qualityDashboard');
  const {
    activePreset,
    canManageSystemPresets,
    isSharing,
    isMakingSystem,
    remove,
    setPinned,
    share,
    makeSystem,
    saveCopy,
  } = useDashboardPresets();

  // Страница уходит к стандартному дашборду только с удалением: отказ сервера в удалении серверного
  // дашборда оставляет его применённым, и уйти со страницы значило бы снять его
  const handleDelete = useCallback(() => {
    if (!activePreset) return;
    remove(activePreset.id, () => onActivePresetChange(null));
  }, [activePreset, remove, onActivePresetChange]);

  const handleSaveCopy = useCallback(() => {
    if (!activePreset) return;
    const copyId = saveCopy(activePreset.id, t('presets.copyName', { name: activePreset.name }));
    if (copyId) onActivePresetChange(copyId);
  }, [activePreset, saveCopy, onActivePresetChange, t]);

  const pinLabel = activePreset?.pinned ? t('presets.unpinFromMenu') : t('presets.pinToMenu');
  const access = activePreset && getPresetAccess(activePreset, canManageSystemPresets);

  return (
    <div className="flex items-center gap-1">
      <IconButton
        variant="ghost"
        size="icon"
        label={t('presets.create')}
        icon={<PlusIcon className="size-4" />}
        onClick={onCreatePreset}
      />

      {activePreset && access && (
        <>
          {activePreset.isEditable ? (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('presets.edit')}
              icon={<PencilIcon className="size-4" />}
              onClick={() => onEditPreset(activePreset)}
            />
          ) : (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('presets.saveCopy')}
              icon={<CopyIcon className="size-4" />}
              onClick={handleSaveCopy}
            />
          )}
          {/* Системный дашборд сразу виден всем, а вернуть его в личные нельзя — поэтому с подтверждением */}
          {access.canMakeSystem && (
            <ConfirmationButton
              label={t('presets.makeSystem')}
              title={t('presets.makeSystemConfirmTitle', { name: activePreset.name })}
              description={t('presets.makeSystemConfirmDescription')}
              onConfirm={() => makeSystem(activePreset.id)}
              isLoadingConfirm={isMakingSystem}
              trigger={{
                size: 'icon',
                color: 'primary',
                icon: <UsersIcon className="size-4" />,
                disabled: isMakingSystem,
              }}
            />
          )}
          {access.canShare && (
            <SharePresetDialog
              title={t('presets.shareTitle', { name: activePreset.name })}
              triggerLabel={t('presets.share')}
              recipients={activePreset.isShared ? activePreset.recipients : []}
              onShare={(recipientIds, onShared) => share(activePreset.id, recipientIds, onShared)}
              isSharing={isSharing}
            />
          )}
          {access.canPinAndRemove && (
            <IconButton
              variant="ghost"
              size="icon"
              label={pinLabel}
              aria-pressed={activePreset.pinned}
              icon={<StarIcon className={cn('size-4', activePreset.pinned && 'fill-current')} />}
              onClick={() => setPinned(activePreset.id, !activePreset.pinned)}
            />
          )}
          {access.canPinAndRemove && (
            <ConfirmationButton
              label={access.isRemovedForSelfOnly ? t('presets.removeShared') : t('presets.deletePreset')}
              title={
                access.isRemovedForSelfOnly
                  ? t('presets.removeSharedConfirmTitle', { name: activePreset.name })
                  : t('presets.deleteConfirmTitle', { name: activePreset.name })
              }
              description={
                access.isRemovedForSelfOnly
                  ? t('presets.removeSharedConfirmDescription')
                  : t('presets.deleteConfirmDescription')
              }
              onConfirm={handleDelete}
              trigger={{ size: 'icon' }}
            />
          )}
        </>
      )}
    </div>
  );
};
